import { ZombieConfig, BossConfig, BossVisual, BossWeakPoint } from '../types';

export const ZOMBIES: Record<string, ZombieConfig> = {
  normal: {
    id: 'normal',
    name: 'Normal Zombie',
    hp: 30,
    damage: 10,
    speed: 1.5,
    detectionRange: 15,
    attackRange: 2.5,
    reward: 10,
    minStage: 1,
  },
  tank: {
    id: 'tank',
    name: 'Tank Zombie',
    hp: 150,
    damage: 25,
    speed: 0.8,
    detectionRange: 12,
    attackRange: 3.0,
    reward: 30,
    minStage: 3,
  },
  spitter: {
    id: 'spitter',
    name: 'Spitter Zombie',
    hp: 40,
    damage: 12,
    speed: 1.8,
    detectionRange: 20,
    attackRange: 10,
    reward: 20,
    minStage: 4,
  },
  exploder: {
    id: 'exploder',
    name: 'Exploder Zombie',
    hp: 30,
    damage: 15,
    speed: 2.0,
    detectionRange: 15,
    attackRange: 3,
    reward: 25,
    minStage: 5,
  },
  crawler: {
    id: 'crawler',
    name: 'Crawler Zombie',
    hp: 25,
    damage: 8,
    speed: 2.2,
    detectionRange: 10,
    attackRange: 1.2,
    reward: 12,
    minStage: 3,
  },
};

export const BOSSES: BossConfig[] = [
  {
    id: 'doctorZero', name: 'Dr. Zero', hp: 500, damage: 30, speed: 1.5,
    detectionRange: 25, attackRange: 2, reward: 200, minStage: 1, stage: 1,
    abilities: ['syringeAttack'],
    visual: {
      bodyColor: 0x334433, skinColor: 0x88aa88, emissiveColor: 0x44ff44,
      crownColor: 0x44ff44, auraColor: 0x00ff44, hornColor: 0x226622,
      scale: 1.0, torsoWidthMult: 0.9, headSizeMult: 1.15, hunchAngle: 0.1,
    },
    weakPoint: { zone: 'head', damageMultiplier: 3.0, glowColor: 0x44ff44 },
  },
  {
    id: 'motherZombie', name: 'Mother Zombie', hp: 700, damage: 20, speed: 1.2,
    detectionRange: 25, attackRange: 3, reward: 250, minStage: 2, stage: 2,
    abilities: ['spawnMinions'],
    abilityCooldown: 10,
    visual: {
      bodyColor: 0x6b2050, skinColor: 0x9a6080, emissiveColor: 0xff66aa,
      crownColor: 0xff44aa, auraColor: 0xff2288, hornColor: 0x882255,
      scale: 1.1, torsoWidthMult: 1.3, headSizeMult: 0.95, hunchAngle: 0.05,
    },
    weakPoint: { zone: 'belly', damageMultiplier: 3.0, glowColor: 0xff66aa },
  },
  {
    id: 'armoredBeast', name: 'Armored Beast', hp: 1000, damage: 35, speed: 2.0,
    detectionRange: 20, attackRange: 2.5, reward: 300, minStage: 3, stage: 3,
    abilities: ['frontArmor', 'charge'],
    visual: {
      bodyColor: 0x555555, skinColor: 0x777777, emissiveColor: 0xffaa00,
      crownColor: 0xcc8800, auraColor: 0xff6600, hornColor: 0x333333,
      scale: 1.3, torsoWidthMult: 1.5, headSizeMult: 0.85, hunchAngle: 0.3,
    },
    weakPoint: { zone: 'back', damageMultiplier: 3.5, glowColor: 0xffaa00 },
  },
  {
    id: 'toxicKing', name: 'Toxic King', hp: 800, damage: 25, speed: 1.5,
    detectionRange: 25, attackRange: 8, reward: 300, minStage: 4, stage: 4,
    abilities: ['poisonCloud', 'acidSpit'],
    visual: {
      bodyColor: 0x225533, skinColor: 0x33aa44, emissiveColor: 0x66ff00,
      crownColor: 0x66ff00, auraColor: 0x44ff00, hornColor: 0x114411,
      scale: 1.05, torsoWidthMult: 1.2, headSizeMult: 1.1, hunchAngle: 0.2,
    },
    weakPoint: { zone: 'chest', damageMultiplier: 3.0, glowColor: 0x66ff00 },
  },
  {
    id: 'cyberZombie', name: 'Cyber Zombie', hp: 1200, damage: 40, speed: 2.0,
    detectionRange: 30, attackRange: 15, reward: 350, minStage: 5, stage: 5,
    abilities: ['laserAttack', 'shield'],
    visual: {
      bodyColor: 0x223344, skinColor: 0x556688, emissiveColor: 0x00ccff,
      crownColor: 0x00aaff, auraColor: 0x0088ff, hornColor: 0x112233,
      scale: 1.05, torsoWidthMult: 1.0, headSizeMult: 1.2, hunchAngle: 0.05,
    },
    weakPoint: { zone: 'back', damageMultiplier: 3.0, glowColor: 0x00ccff },
  },
  {
    id: 'hiveMind', name: 'Hive Mind', hp: 900, damage: 20, speed: 3.0,
    detectionRange: 30, attackRange: 2, reward: 350, minStage: 6, stage: 6,
    abilities: ['teleport', 'commandZombies'],
    visual: {
      bodyColor: 0x442266, skinColor: 0x8844aa, emissiveColor: 0xcc44ff,
      crownColor: 0xaa33ff, auraColor: 0x8800ff, hornColor: 0x331155,
      scale: 0.95, torsoWidthMult: 0.85, headSizeMult: 1.4, hunchAngle: 0.1,
    },
    weakPoint: { zone: 'head', damageMultiplier: 4.0, glowColor: 0xcc44ff },
  },
  {
    id: 'fleshGolem', name: 'Flesh Golem', hp: 1500, damage: 50, speed: 0.8,
    detectionRange: 20, attackRange: 3, reward: 400, minStage: 7, stage: 7,
    abilities: ['shockwave', 'groundSlam'],
    visual: {
      bodyColor: 0x664433, skinColor: 0x885544, emissiveColor: 0xff4400,
      crownColor: 0xcc3300, auraColor: 0xff2200, hornColor: 0x442211,
      scale: 1.4, torsoWidthMult: 1.6, headSizeMult: 0.8, hunchAngle: 0.35,
    },
    weakPoint: { zone: 'belly', damageMultiplier: 3.5, glowColor: 0xff4400 },
  },
  {
    id: 'shadowStalker', name: 'Shadow Stalker', hp: 600, damage: 35, speed: 5.0,
    detectionRange: 30, attackRange: 1.5, reward: 400, minStage: 8, stage: 8,
    abilities: ['invisibility', 'backstab'],
    visual: {
      bodyColor: 0x111111, skinColor: 0x222222, emissiveColor: 0x8800aa,
      crownColor: 0x440066, auraColor: 0x660088, hornColor: 0x0a0a0a,
      scale: 0.9, torsoWidthMult: 0.75, headSizeMult: 1.0, hunchAngle: 0.4,
    },
    weakPoint: { zone: 'legs', damageMultiplier: 3.0, glowColor: 0x8800aa },
  },
  {
    id: 'generalDead', name: 'General Dead', hp: 1300, damage: 30, speed: 1.5,
    detectionRange: 30, attackRange: 20, reward: 450, minStage: 9, stage: 9,
    abilities: ['gunAttack', 'commandSquad'],
    visual: {
      bodyColor: 0x3a4a2a, skinColor: 0x6a7a5a, emissiveColor: 0xffcc00,
      crownColor: 0xffdd00, auraColor: 0xffaa00, hornColor: 0x4a4a2a,
      scale: 1.1, torsoWidthMult: 1.15, headSizeMult: 0.95, hunchAngle: 0.08,
    },
    weakPoint: { zone: 'chest', damageMultiplier: 3.0, glowColor: 0xffcc00 },
  },
  {
    id: 'apocalypseZombie', name: 'Apocalypse Zombie', hp: 2000, damage: 45, speed: 2.0,
    detectionRange: 35, attackRange: 5, reward: 500, minStage: 10, stage: 10,
    abilities: ['randomBossAbility'],
    visual: {
      bodyColor: 0x440000, skinColor: 0x882222, emissiveColor: 0xff0000,
      crownColor: 0xff0000, auraColor: 0xff0000, hornColor: 0x220000,
      scale: 1.25, torsoWidthMult: 1.3, headSizeMult: 1.1, hunchAngle: 0.2,
    },
    weakPoint: { zone: 'chest', damageMultiplier: 2.5, glowColor: 0xff0000 },
  },
];
